import { Flex, Text, Tooltip } from "@chakra-ui/react";
import { Layout } from "@/components/Layout/Layout";
import { BaseTableFilters } from "@/components/BaseTableFilters/BaseTableFilters";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { Pagination } from "@/components/Pagination/Pagination";
import { createColumnHelper } from "@tanstack/react-table";
import { useState, useMemo } from "react";
import { formatDate } from "@/helpers/formatDate";
import { mockLeadContacts, LeadContact } from "./mockData";
import { useNavigate } from "react-router-dom";

export const LeadsContactsPage = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const columnHelper = createColumnHelper<LeadContact>();

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: "Имя",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="180px" fontSize={14} fontWeight={600} color="#3b515e">
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 180,
      minSize: 150,
    }),
    columnHelper.accessor("position", {
      id: "position",
      header: "Должность",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="200px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 200,
      minSize: 150,
    }),
    columnHelper.accessor("work_phone", {
      id: "work_phone",
      header: "Рабочий телефон",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="150px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 150,
      minSize: 120,
    }),
    columnHelper.accessor("work_email", {
      id: "work_email",
      header: "Рабочая почта",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="200px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 200,
      minSize: 150,
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: "Дата создания",
      cell: (info) => (
        <Tooltip label={formatDate(info.getValue() || "")} hasArrow>
          <Text noOfLines={1} maxW="120px" fontSize={14}>
            {formatDate(info.getValue() || "")}
          </Text>
        </Tooltip>
      ),
      enableSorting: true,
      size: 120,
      minSize: 100,
    }),
  ];

  // Фильтрация данных на основе поиска
  const filteredContacts = useMemo(() => {
    return mockLeadContacts.filter((contact) => {
      const matchesSearch =
        !searchValue ||
        contact.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        contact.position.toLowerCase().includes(searchValue.toLowerCase()) ||
        contact.work_email.toLowerCase().includes(searchValue.toLowerCase());
      return matchesSearch;
    });
  }, [searchValue]);

  // Пагинация данных
  const paginatedContacts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredContacts.slice(startIndex, endIndex);
  }, [filteredContacts, currentPage, itemsPerPage]);

  const totalItems = filteredContacts.length;

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1); // Сброс на первую страницу при поиске
  };

  const handlePageChange = (offset: number, limit: number) => {
    const newPage = offset / limit + 1;
    setCurrentPage(newPage);
    setItemsPerPage(limit);
  };

  const handleCreateContact = () => {
    navigate("/leads_contacts/create");
  };

  return (
    <Layout>
      <Flex flexDirection="column" maxH="90vh" overflowY="auto" gap="24px">
        <Text fontSize={27} fontWeight={500}>
          Контакты
        </Text>

        {/* Контейнер для фильтров */}
        <BaseTableFilters
          handleSearch={handleSearch}
          placeHolderInput="Поиск по контактам"
          searchValue={searchValue}
          isShowBtnPopupFilters={false}
          isShowCreateButton
          onClickCreate={handleCreateContact}
          isLoading={false}
        />

        {/* Таблица */}
        <BaseTable list={paginatedContacts} columns={columns} isLoading={false} />

        {/* Пагинация */}
        <Pagination totalItems={totalItems} onPageChange={handlePageChange} isLoading={false} />
      </Flex>
    </Layout>
  );
};
